// =====================================================================
// EncounterForm (PME Sprint 2 / S51) — formulário de consulta (SOAP).
// Presentational: estado + validação + UI. O pai (EncounterPage) cuida de
// API (create/update/sign) + navegação. Usado em nova consulta e edição.
//
// Contrato backend (EncounterService.WRITABLE_FIELDS):
//   professionalId, appointmentId, encounterDate, encounterType,
//   subjective, objective, assessment, plan, cidCodes(array), vitalSigns(JSONB)
//   (companyId/patientId/createdBy/status vêm do servidor, nunca do body)
// =====================================================================
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Chip
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import LockIcon from "@material-ui/icons/Lock";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import { ENCOUNTER_TYPES } from "../../services/encounterService";
import ScribePanel from "./ScribePanel";

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600
  },
  soapHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: 2
  },
  signedBanner: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    background: theme.palette.action.hover
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  signBtn: {
    marginRight: "auto"
  }
}));

const pad = (n) => String(n).padStart(2, "0");

const nowLocalDatetime = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toLocalDatetime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// "YYYY-MM-DDTHH:mm" -> "DD/MM/YYYY às HH:mm" (exibição read-only da data do atendimento)
const displayDateTime = (local) => {
  if (!local) return "—";
  const [datePart, timePart] = String(local).split("T");
  if (!datePart) return "—";
  const [y, m, d] = datePart.split("-");
  return `${d}/${m}/${y}${timePart ? " às " + timePart : ""}`;
};

const computeImc = (peso, altura) => {
  const p = parseFloat(String(peso).replace(",", "."));
  const aCm = parseFloat(String(altura).replace(",", "."));
  if (!p || !aCm) return "";
  const m = aCm / 100;
  if (m <= 0) return "";
  return (p / (m * m)).toFixed(1);
};

// Estado nutricional — classificação padrão por IMC (sugestão automática, editável pela Dra).
const NUTRI_OPTIONS = [
  "Baixo peso",
  "Eutrófico",
  "Sobrepeso",
  "Obesidade grau I",
  "Obesidade grau II",
  "Obesidade grau III",
  "Não avaliado"
];

const imcToNutri = (imc) => {
  const n = parseFloat(String(imc).replace(",", "."));
  if (!n) return "";
  if (n < 18.5) return "Baixo peso";
  if (n < 25) return "Eutrófico";
  if (n < 30) return "Sobrepeso";
  if (n < 35) return "Obesidade grau I";
  if (n < 40) return "Obesidade grau II";
  return "Obesidade grau III";
};

// encounter (camelCase do backend) -> estado plano do form
const flatten = (e) => {
  const v = (e && e.vitalSigns) || {};
  const cids = (e && e.cidCodes) || [];
  return {
    encounterDate: e && e.encounterDate ? toLocalDatetime(e.encounterDate) : nowLocalDatetime(),
    encounterType: (e && e.encounterType) || "first_visit",
    professionalId: e && e.professionalId ? String(e.professionalId) : "",
    appointmentId: e && e.appointmentId ? String(e.appointmentId) : "",
    vPa: v.pa || "",
    vFc: v.fc || "",
    vPeso: v.peso || "",
    vAltura: v.altura || "",
    vGlicemia: v.glicemia || "",
    vTemp: v.temp || "",
    vEstadoNutricional: v.estadoNutricional || "",
    subjective: (e && e.subjective) || "",
    objective: (e && e.objective) || "",
    assessment: (e && e.assessment) || "",
    plan: (e && e.plan) || "",
    cid: Array.isArray(cids) ? cids.join(", ") : ""
  };
};

// estado plano -> payload pro backend
const buildPayload = (f) => {
  const imcCalc = computeImc(f.vPeso, f.vAltura);
  const vitalSigns = {
    pa: f.vPa,
    fc: f.vFc,
    peso: f.vPeso,
    altura: f.vAltura,
    imc: imcCalc,
    glicemia: f.vGlicemia,
    temp: f.vTemp,
    estadoNutricional: f.vEstadoNutricional || imcToNutri(imcCalc)
  };
  const hasVitals = Object.values(vitalSigns).some((v) => v && String(v).trim() !== "");
  const cidCodes = f.cid
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    encounterDate: f.encounterDate ? new Date(f.encounterDate).toISOString() : null,
    encounterType: f.encounterType,
    professionalId: f.professionalId ? Number(f.professionalId) : null,
    appointmentId: f.appointmentId ? Number(f.appointmentId) : null,
    subjective: f.subjective,
    objective: f.objective,
    assessment: f.assessment,
    plan: f.plan,
    cidCodes,
    vitalSigns: hasVitals ? vitalSigns : null
  };
};

const formatApptLabel = (a) => {
  const dt = a.datetimeStart ? new Date(a.datetimeStart) : null;
  const when = dt && !isNaN(dt.getTime())
    ? `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`
    : "—";
  return `${when} · ${a.procedureType || "consulta"}${a.professionalName ? " · " + a.professionalName : ""}`;
};

const EncounterForm = ({
  mode = "create",
  initialEncounter = null,
  patientId = null,
  professionals = [],
  appointments = [],
  saving = false,
  readOnly = false,
  onSave,
  onCancel,
  onSign
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [form, setForm] = useState(() => flatten(initialEncounter));
  const [aiScribeSessionId, setAiScribeSessionId] = useState(null);

  // Recebe o rascunho do Clara Scribe e preenche os campos do form (editáveis).
  const handleScribeDraft = (draft, sessionId) => {
    const v = (draft && draft.vitaisExtraidos) || {};
    setForm((prev) => ({
      ...prev,
      subjective: draft.subjective || prev.subjective,
      objective: draft.objective || prev.objective,
      assessment: draft.assessment || prev.assessment,
      plan: draft.plan || prev.plan,
      cid:
        Array.isArray(draft.cidSugerido) && draft.cidSugerido.length
          ? draft.cidSugerido
              .map((c) => (typeof c === "string" ? c : c && c.codigo))
              .filter(Boolean)
              .join(", ")
          : prev.cid,
      vPa: v.pa || prev.vPa,
      vFc: v.fc || prev.vFc,
      vPeso: v.peso || prev.vPeso,
      vAltura: v.altura || prev.vAltura,
      vGlicemia: v.glicemia || prev.vGlicemia,
      vTemp: v.temp || prev.vTemp,
      vEstadoNutricional: v.estadoNutricional || prev.vEstadoNutricional
    }));
    setAiScribeSessionId(sessionId);
  };

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const imc = computeImc(form.vPeso, form.vAltura);
  const nutriSuggestion = imcToNutri(imc);
  const nutriValue = form.vEstadoNutricional || nutriSuggestion;
  const ro = readOnly;

  const handleSubmit = () => {
    onSave(buildPayload(form), aiScribeSessionId);
  };

  return (
    <>
      {ro && (
        <Paper className={classes.signedBanner} variant="outlined">
          <LockIcon fontSize="small" color="action" />
          <Typography variant="body2" color="textSecondary">
            Consulta <strong>assinada</strong> — registro do prontuário em modo leitura (não pode mais ser editada).
          </Typography>
        </Paper>
      )}

      {!ro && (
        <ScribePanel
          patientId={patientId}
          professionalId={form.professionalId}
          professionals={professionals}
          onProfessionalChange={(id) => setForm((prev) => ({ ...prev, professionalId: id }))}
          encounterId={initialEncounter && initialEncounter.id}
          onDraft={handleScribeDraft}
        />
      )}

      {/* ---------- Seção 1: Dados da consulta ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          Dados da consulta
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Atendimento"
              value={displayDateTime(form.encounterDate)}
              InputProps={{ readOnly: true }}
              helperText={mode === "create" ? "registrado agora (automático)" : " "}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Tipo"
              value={form.encounterType}
              onChange={set("encounterType")}
              disabled={ro}
              fullWidth
              variant="outlined"
              size="small"
            >
              {ENCOUNTER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Profissional"
              value={form.professionalId}
              onChange={set("professionalId")}
              disabled={ro}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="">—</MenuItem>
              {professionals.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Vincular a agendamento"
              value={form.appointmentId}
              onChange={set("appointmentId")}
              disabled={ro}
              helperText={appointments.length === 0 ? "Sem agendamentos do paciente" : " "}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="">— Nenhum</MenuItem>
              {appointments.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {formatApptLabel(a)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => history.push("/agenda-medica")}>
              Abrir agenda médica
            </Button>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
              Agendar, remarcar e marcar retorno é feito na Agenda Médica (sincroniza com o prontuário).
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Seção 2: Sinais vitais ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          Sinais vitais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={2}>
            <TextField label="PA (mmHg)" placeholder="120/80" value={form.vPa} onChange={set("vPa")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="FC (bpm)" type="number" value={form.vFc} onChange={set("vFc")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Peso (kg)" type="number" value={form.vPeso} onChange={set("vPeso")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Altura (cm)" type="number" value={form.vAltura} onChange={set("vAltura")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="IMC" value={imc} placeholder="auto" InputProps={{ readOnly: true }} helperText="calculado" fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Temp (°C)" type="number" value={form.vTemp} onChange={set("vTemp")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Glicemia (mg/dL)" type="number" value={form.vGlicemia} onChange={set("vGlicemia")} disabled={ro} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Estado nutricional"
              value={nutriValue}
              onChange={set("vEstadoNutricional")}
              disabled={ro}
              helperText={!form.vEstadoNutricional && nutriSuggestion ? "sugerido pelo IMC (pode trocar)" : " "}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="">—</MenuItem>
              {NUTRI_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Seção 3: SOAP ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          Evolução (SOAP)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="S — Subjetivo (queixa, história do paciente)" value={form.subjective} onChange={set("subjective")} disabled={ro} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="O — Objetivo (exame físico, achados)" value={form.objective} onChange={set("objective")} disabled={ro} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="A — Avaliação (hipóteses, diagnóstico)" value={form.assessment} onChange={set("assessment")} disabled={ro} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="P — Plano / Conduta" value={form.plan} onChange={set("plan")} disabled={ro} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="CID-10 (separe por vírgula)"
              placeholder="ex: E11.9, I10"
              value={form.cid}
              onChange={set("cid")}
              disabled={ro}
              fullWidth
              variant="outlined"
              size="small"
            />
            {form.cid.trim() !== "" && (
              <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {form.cid.split(",").map((c) => c.trim()).filter(Boolean).map((c, i) => (
                  <Chip key={i} label={c} size="small" />
                ))}
              </div>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Ações ---------- */}
      <div className={classes.actions}>
        {!ro && mode === "edit" && onSign && (
          <Button variant="outlined" color="primary" className={classes.signBtn} startIcon={<CheckCircleOutlineIcon />} onClick={onSign} disabled={saving}>
            Assinar consulta
          </Button>
        )}
        <Button onClick={onCancel} disabled={saving}>
          {ro ? "Voltar" : "Cancelar"}
        </Button>
        {!ro && (
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        )}
      </div>
    </>
  );
};

export default EncounterForm;
